import React, { useRef, useState, useEffect, useCallback } from 'react';
import classes from './Register.module.css';
import fl_logo from '../../assets/fl_logo.png';
import CustomButton from '../../ui/CustomButton';
import CustomSpinner from '../../ui/CustomSpinner';
import useV3Axios from '../../hooks/useV3Axios';
import { useHistory, useLocation } from 'react-router-dom';
import { REGISTER_COMPLETE_PATH, REGISTER_PATH, LOGIN_PATH } from '../../utils/paths';

const RESEND_COOLDOWN_SECONDS = 60;

const RegisterVerify = () => {
    const history = useHistory();
    const location = useLocation();
    const email = location.state?.email;

    const otpRef = useRef(null);
    const [error, setError] = useState(null);
    const [countdown, setCountdown] = useState(RESEND_COOLDOWN_SECONDS);

    const { isLoading, error: axiosError, data, sendRequest } = useV3Axios();
    const { isLoading: isResending, error: resendError, data: resendData, sendRequest: sendResend } = useV3Axios();

    // Redirect if arrived without email in state
    useEffect(() => {
        if (!email) history.replace(REGISTER_PATH);
    }, [email, history]);

    // Countdown timer for resend
    useEffect(() => {
        if (countdown <= 0) return;
        const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
        return () => clearTimeout(timer);
    }, [countdown]);

    // On verify success
    useEffect(() => {
        if (data) {
            history.push(REGISTER_COMPLETE_PATH, {
                email,
                emailVerifiedToken: data.emailVerifiedToken,
            });
        }
    }, [data, history, email]);

    // On verify error
    useEffect(() => {
        if (axiosError) setError(axiosError);
    }, [axiosError]);

    // On resend success
    useEffect(() => {
        if (resendData) {
            setCountdown(RESEND_COOLDOWN_SECONDS);
            setError(null);
        }
    }, [resendData]);

    useEffect(() => {
        if (resendError) setError(resendError);
    }, [resendError]);

    const handleVerify = useCallback((event) => {
        event.preventDefault();
        setError(null);
        sendRequest('/register/verify-otp', {
            email,
            otp: otpRef.current.value.trim(),
        });
    }, [sendRequest, email]);

    const handleResend = useCallback(() => {
        if (countdown > 0) return;
        setError(null);
        sendResend('/register/send-otp', { email });
    }, [sendResend, email, countdown]);

    if (!email) return null;

    return (
        <div className={classes.Login}>
            <div className={classes.Card}>
                <img className={classes.Logo} src={fl_logo} alt="logo" />
                <p className={classes.Title}>FetalLite Dashboard</p>
                <form className={classes.Form} onSubmit={handleVerify}>
                    <h3 style={{ textAlign: 'left', margin: '0 0 8px 0', color: '#333' }}>Verify your email</h3>
                    <p style={{ textAlign: 'left', fontSize: '14px', color: '#555', margin: '0 0 16px 0' }}>
                        Enter the 6-digit code sent to <strong>{email}</strong>
                    </p>
                    <input
                        ref={otpRef}
                        className={classes.OtpInput}
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="------"
                        autoFocus
                        required
                    />
                    <div className={classes.ResendRow}>
                        <span>Didn't receive it?</span>
                        {countdown > 0
                            ? <span style={{ color: '#999' }}>Resend in {countdown}s</span>
                            : <span
                                className={classes.Link}
                                onClick={handleResend}
                                style={{ opacity: isResending ? 0.5 : 1 }}
                            >
                                {isResending ? 'Sending...' : 'Resend OTP'}
                            </span>
                        }
                    </div>
                    {error && <p className={classes.Error}>{error}</p>}
                    <div className={classes.Container}>
                        {isLoading ? <CustomSpinner /> : <CustomButton type='submit' size='large'>VERIFY</CustomButton>}
                    </div>
                    <hr className={classes.Divider} />
                    <p style={{ textAlign: 'center', fontSize: '14px', color: '#555' }}>
                        <span className={classes.Link} onClick={() => history.push(LOGIN_PATH)}>Back to sign in</span>
                    </p>
                </form>
            </div>
        </div>
    );
};

export default RegisterVerify;
